import React, { useState } from "react";
import { Button, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { resolveAuthClientError } from "../../helpers/authClientError";
import { getGoogleAuthorizationUrl } from "../../services/authService";
import { GoogleLogo } from "./GoogleLogo";

const GOOGLE_INTENT_KEY = "adatis.googleOAuthIntent";

export type GoogleAuthIntent = "login" | "signup" | "link";

interface GoogleAuthButtonProps {
  intent?: GoogleAuthIntent;
  className?: string;
  disabled?: boolean;
  labelKey?: string;
}

export function setGoogleOAuthIntent(intent: GoogleAuthIntent): void {
  try {
    sessionStorage.setItem(GOOGLE_INTENT_KEY, intent);
  } catch {
    // sessionStorage unavailable
  }
}

export function consumeGoogleOAuthIntent(): GoogleAuthIntent {
  try {
    const value = sessionStorage.getItem(GOOGLE_INTENT_KEY);
    sessionStorage.removeItem(GOOGLE_INTENT_KEY);

    if (value === "signup" || value === "link") {
      return value;
    }

    return "login";
  } catch {
    return "login";
  }
}

const GoogleAuthButton = ({
  intent = "login",
  className = "",
  disabled = false,
  labelKey,
}: GoogleAuthButtonProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const label = t(
    labelKey ||
      (intent === "link"
        ? "settings.account.google.link"
        : "auth.google.actions.continue")
  );

  const handleClick = async () => {
    setLoading(true);

    try {
      setGoogleOAuthIntent(intent);
      const response = await getGoogleAuthorizationUrl();

      if (!response.success || !response.data?.authorization_url) {
        toast.error(t("auth.google.toast.startFailed"));
        setLoading(false);
        return;
      }

      window.location.assign(response.data.authorization_url);
    } catch (error) {
      const resolved = resolveAuthClientError(
        error,
        t("auth.google.toast.startFailed")
      );
      toast.error(resolved.message);
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      color="light"
      className={`w-100 border d-inline-flex align-items-center justify-content-center gap-2 ${className}`.trim()}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-label={label}
    >
      {loading ? (
        <>
          <Spinner size="sm" />
          {t("auth.google.actions.loading")}
        </>
      ) : (
        <>
          <GoogleLogo size={18} />
          <span>{label}</span>
        </>
      )}
    </Button>
  );
};

export default GoogleAuthButton;
